import { AfterViewInit, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { cloneDeep } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import {
  AccountService,
  AlertService,
  CampaignProductService,
  CampaignService,
  CampaignTrackingService,
  CancelReasonCategoryService,
  CRMCampaignService,
  FunnelService,
  FunnelStepService,
  GuiService,
  MerchantService,
  ReportsConfigService,
  ResolveReportService,
  SideNavService,
  StorageService,
  UserService,
} from '../_services';
import { MultiOptions } from '../custom-multi-select/multi-select';
import { expandCollapse } from '../_shared';
import { ReportsAbstractV3Component } from './reports-abstract-v3.component';
import {
  reportsFieldDataV3,
  ResolveFields,
  ResolveFilters,
  resolveMetrics,
  resolveMomentColumns,
  resolveStringFilterDimensions,
  resolveSummaryMetrics,
  resolveTopIntentColumns,
} from './utils';
import {
  ReportDimensions,
  ReportFilterValues,
  ReportPanelColumn,
  ReportTypes,
  ResolveSummaryResponse,
} from './models/reports';

@Component({
  moduleId: module.id.toString(),
  selector: 'resolve-report-v3',
  templateUrl: './templates/reports-v3.component.html',
  styleUrls: ['./styles/reports-v2.component.scss', './styles/reports-v3.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class ResolveReportV3Component extends ReportsAbstractV3Component implements OnInit, AfterViewInit {
  fields: MultiOptions = cloneDeep(ResolveFields);
  filters: MultiOptions = cloneDeep(ResolveFilters);
  reportType = ReportTypes.RESOLVE;
  showSummaryRow: boolean = true;
  showOverviewPanel: boolean = true;
  showPortalMomentsPanel: boolean = true;
  showTopIntentsPanel: boolean = true;
  summaryExpanded: boolean = true;
  momentsPanelTitle: string = 'Top Moments';
  momentsPanelColumns: ReportPanelColumn[] = resolveMomentColumns;
  topIntentsPanelColumns: ReportPanelColumn[] = resolveTopIntentColumns;
  showExcludeUpsellsFilter: boolean = false;
  showExcludeValidationsFilter: boolean = false;
  showExcludeDuplicateViewsFilter: boolean = true;
  dropdownsLoading: boolean = false;

  predefinedFields: string[] = [];
  commonReportsFields: string[] = [...resolveMetrics];
  summaryFields: string[] = [...resolveSummaryMetrics];

  selectedDimensions = [{ model: ReportDimensions.Campaign, label: 'Campaign', selected: true, disabled: false }];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    public reportsService: ResolveReportService,
    protected modalService: NgxSmartModalService,
    protected campaignService: CampaignService,
    protected reportsConfigService: ReportsConfigService,
    protected trackingSourceService: CampaignTrackingService,
    protected userService: UserService,
    protected accountService: AccountService,
    protected campaignProductService: CampaignProductService,
    protected crmCampaignService: CRMCampaignService,
    protected storageService: StorageService,
    protected stepService: FunnelStepService,
    protected merchantService: MerchantService,
    protected cancelReasonCategoryService: CancelReasonCategoryService,
    protected funnelService: FunnelService,
    protected guiService: GuiService,
    protected sideNavService: SideNavService
  ) {
    super(
      router,
      location,
      route,
      alertService,
      reportsService,
      campaignService,
      reportsConfigService,
      trackingSourceService,
      userService,
      accountService,
      campaignProductService,
      crmCampaignService,
      storageService,
      stepService,
      merchantService,
      modalService,
      cancelReasonCategoryService,
      funnelService,
      guiService
    );
    this.fieldData = reportsFieldDataV3;
    this.updatePreSelectedDimensions(ReportDimensions.Campaign);
    this.bannerTitle = 'Resolve Report';
    this.bannerSubtitle =
      'Each row is one digital-receipt lookup from a bank app. Matched means the receipt found a real order; ' +
      'Link Clicks counts the cardholders who opened it. Test lookups are excluded.';
  }

  ngOnInit() {
    super.ngOnInit();
    this.sideNavService.setSideNavExpanded(false);
  }

  ngAfterViewInit() {
    if (this.panel) {
      this.panel.expanded = true;
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.selectedDimensions, event.previousIndex, event.currentIndex);
  }

  getQueryForReport() {
    const query = super.getQueryForReport();
    query['report_type'] = this.reportType;
    query['show_percentage'] = this.showPercentage;
    this.applyCampaignFilter(query['filters']);
    this.applyExcludeDuplicateViews(query['filters']);
    return query;
  }

  getQueryForSummary() {
    const query = super.getQueryForSummary();
    query['report_type'] = this.reportType;
    query['fields'] = [];
    this.applyCampaignFilter(query['filters']);
    return query;
  }

  getSummary() {
    this.isSummaryLoading = true;
    this.isOverviewLoading = true;
    this.reportsService
      .getSummary(this.getQueryForSummary())
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (result) => {
          this.applySummaryResponse(result && result[0] ? result[0] : {});
          this.stopSummaryLoading();
        },
        (error) => {
          this.applySummaryResponse({});
          this.stopSummaryLoading();
          this.handleError(error);
        }
      );
  }

  getDateDependentFiltersData() {}

  protected getFilterOptionConfig(): ReportDimensions[] {
    return [...resolveStringFilterDimensions];
  }

  getFilterOptionsData(fields: ReportDimensions[]) {
    super.getFilterOptionsData(fields);
    this.fetchGroupedFilterOptions(ReportDimensions.Campaign, ReportDimensions.CampaignID, ReportDimensions.Brand);
    this.fetchGroupedFilterOptions(
      ReportDimensions.MerchantProfile,
      ReportDimensions.MerchantProfileID,
      ReportDimensions.MerchantProfileName
    );
    this.fetchGroupedFilterOptions(ReportDimensions.Product, ReportDimensions.ProductID, ReportDimensions.ProductName);
  }

  private applySummaryResponse(response: Partial<ResolveSummaryResponse>) {
    const overview = response.overview ? response.overview : {};
    const moments = Array.isArray(response.top_moments) ? response.top_moments : [];
    const intents = Array.isArray(response.top_intents) ? response.top_intents : [];

    const summary = this.buildSummaryData(overview);
    this.summary = summary;
    this.setOverviewCards(this.buildOverviewCards(resolveSummaryMetrics, overview, summary));
    this.portalMoments = moments;
    this.topIntents = intents;
  }

  private stopSummaryLoading() {
    this.isSummaryLoading = false;
    this.isOverviewLoading = false;
  }

  private applyCampaignFilter(filters: ReportFilterValues) {
    if (filters && filters[ReportDimensions.CampaignID]) {
      if (!filters[ReportDimensions.Campaign]) {
        filters[ReportDimensions.Campaign] = filters[ReportDimensions.CampaignID];
      }
      delete filters[ReportDimensions.CampaignID];
    }
  }

  private applyExcludeDuplicateViews(filters: ReportFilterValues) {
    if (filters && this.showExcludeDuplicateViewsFilter && this.excludeDuplicateViews) {
      filters[ReportDimensions.ExcludeDuplicateViews] = true;
    }
  }
}
